<template>
  <div
    id="business-report-navigation"
    ref="element"
    v-animate-zoom-in-out
    :style="colorTheme"
    class="
      navigation-br fixed right-5 top-5 z-[51] block size-16 max-h-svh grow-0 rounded-[200px]
      transition-colors duration-300
    "
    :class="isOpen ? 'bg-theme-200' : 'bg-blue-500 hover:bg-blue-600'"
  >
    <!-- BURGER BUTTON -->
    <button
      type="button"
      class="absolute inset-0 flex items-center justify-center burger-button"
      :aria-label="$t('aria_labels.open_navigation')"
      aria-controls="business-report-navigation"
      :aria-expanded="isOpen"
      @click="toggle()"
    >
      <IconBurger class="w-8 text-white" />
    </button>

    <Container
      class="w-screen min-w-[97vw] max-w-[97vw] pb-11 pt-28 md:pb-28 md:pt-36"
      :class="isOpen ? 'pointer-events-auto' : 'pointer-events-none'"
      indent="lg"
      :aria-hidden="!isOpen"
    >
      <!-- LANG SWITCHER MOBILE -->
      <div class="absolute top-8 z-[51] slide-up left-container-lg lg:hidden md:left-container-desktop">
        <LangSwitcher />
      </div>

      <!-- CLOSE BUTTON -->
      <button
        :aria-label="$t('close')"
        type="button"
        class="absolute flex items-center gap-3 close-button group right-8 top-8"
        @click="toggle()"
      >
        <span class="pb-1 typo-body-xl max-md:hidden">{{ $t('close') }}</span>
        <IconPlus class="w-10 rotate-45 transition-transform group-hover:rotate-[135deg]" />
      </button>

      <nav class="br-router-link">
        <ul class="grid max-w-container-max gap-x-12 gap-y-26 lg:grid-cols-2">
          <!-- NAVIGATION -->
          <li>
            <h3 class="mb-12 slide-up typo-label-lg max-md:hidden">
              {{ $t('navigation') }}
            </h3>

            <ul v-if="currentBusinessReport" class="grid grid-cols-1 gap-y-6 md:gap-y-12">
              <!-- Current Business Report -->
              <li class="slide-up">
                <NuxtLink
                  :to="currentBusinessReport.url"
                  :class="currentBusinessReport.url !== route.path ? `cursor-pointer hover:text-blue-500` : null"
                  class="transition-colors typo-body-3xl md:typo-body-4xl"
                >
                  {{ currentBusinessReport.title }}
                </NuxtLink>
              </li>

              <!-- Children -->
              <template
                v-for="child in currentBusinessReport.children"
                :key="child.entry.id"
              >
                <li v-if="!child.entry.is_independent" class="slide-up">
                  <NuxtLink
                    :to="child.entry.url"
                    :class="child.entry.url !== route.path ? 'cursor-pointer hover:text-blue-500' : null"
                    class="transition-colors typo-body-3xl md:typo-body-4xl"
                  >
                    {{ child.entry.title }}
                  </NuxtLink>
                </li>
              </template>
            </ul>
          </li>

          <!-- FURTHER YEARS -->
          <li v-if="otherBusinessReports.length">
            <h3 class="slide-up mb-7 typo-label-lg md:mb-12">
              {{ $t('further_years') }}
            </h3>

            <ul class="grid grid-cols-[repeat(auto-fill,_minmax(6rem,_1fr))] gap-y-5 md:grid-cols-1">
              <template
                v-for="report in otherBusinessReports"
                :key="report.id"
              >
                <li class="slide-up">
                  <NuxtLink
                    :to="report.url"
                    class="transition-colors typo-body-3xl hover:text-blue-500 md:typo-body-4xl"
                    :aria-label="report.title"
                  >
                    <span>{{ report.year?.value }}</span>
                  </NuxtLink>
                </li>
              </template>
            </ul>
          </li>
        </ul>
      </nav>

      <!-- BUTTONS -->
      <div class="flex flex-wrap gap-x-10 gap-y-4 pt-24 lg:items-end md:pt-28">
        <div v-if="globals?.business_reports?.link" class="slide-up">
          <AppButton
            icon="default"
            variant="outlined-dark"
            :to="globals?.business_reports?.link"
          >
            {{ globals?.business_reports?.link_text }}
          </AppButton>
        </div>

        <div v-if="currentBusinessReport?.file_financial_statement" class="slide-up">
          <AppButton

            variant="outlined-dark"
            icon="download"
            :to="currentBusinessReport.file_financial_statement.permalink"
          >
            {{ currentBusinessReport.text_financial_statement }}
          </AppButton>
        </div>

        <!-- LANG SWITCHER DESKTOP -->
        <LangSwitcher class="ml-auto slide-up max-lg:hidden" />
      </div>
    </Container>
  </div>
</template>

<script lang="ts" setup>
import gsap from 'gsap'

const globals = inject(GlobalsKey, null)
const { locale } = useI18n()
const route = useRoute()
const element = ref<HTMLElement | null>(null)
const isOpen = ref(false)

// Fetch all business reports of the current site (all years now live in one site per language)
const { data, error } = await useAsyncGql({
  operation: 'BusinessReports',
  variables: { site: locale.value },
})

if (error.value) {
  console.error('Error fetching business reports:', error.value)
}

const currentBusinessReport = computed(() => {
  const currentPath = route.path

  for (const entry of data.value?.entries?.data ?? []) {
    if (entry.url === currentPath) {
      return entry
    }
    for (const child of entry.children || []) {
      if (child.entry.url === currentPath) {
        return entry
      }
    }
  }
  return null // or handle no match as needed
})

const colorTheme = useGetColorThemeStyles(currentBusinessReport)

// All other years of the current report site (every year now lives in the same site)
const otherBusinessReports = computed(() => {
  const allBusinessReports = data.value?.entries.data || []

  return allBusinessReports.filter(
    entry => entry.id !== currentBusinessReport?.value?.id,
  )
})

async function toggle() {
  if (!isOpen.value) {
    show()
  } else {
    hide()
  }
}

let tl: gsap.core.Timeline | null = null
async function show() {
  if (tl?.isActive()) return // Skip if timeline is active

  const burgerButton = element.value?.querySelector('.burger-button') // Select the burger button
  const closeButton = element.value?.querySelector('.close-button') // Select the close button
  const slideUpItems = element.value?.querySelectorAll('.slide-up') // Select all .slide-up elements

  // Create or reset the timeline
  tl = gsap.timeline({
    defaults: {
      duration: 0.6,
      ease: 'power1.inOut',
    },
  })

  tl
    .to(burgerButton, {
      autoAlpha: 0,
      duration: 0.3,
    })
    .to(element.value, { borderRadius: 0 }, '<')
    .call(() => { isOpen.value = !isOpen.value }, null, '<')
    .to(element.value, {
      top: 0,
      right: 0,
    }, '<')
    .to(element.value, { width: '100vw' }, '<')
    .to(element.value, { minHeight: '100svh' }, '<')
    .to(element.value, { height: 'auto' }, '<')
    .to(closeButton, {
      autoAlpha: 1,
      duration: 0.3,
    }, '-=0.1')
    .to(slideUpItems, {
      autoAlpha: 1,
      translateY: 0,
      duration: 0.3,
      stagger: 0.1,
    }, '-=0.2')
    .set(element.value, {
      overflowX: 'auto',
    })

  await tl
}

async function hide() {
  if (!tl) {
    return
  }

  // Wait for the timeline to finish if it's active
  if (tl.isActive()) {
    await new Promise((resolve) => {
      const checkActive = () => {
        if (!tl.isActive()) {
          resolve()
        } else {
          setTimeout(checkActive, 50) // Check every 50ms
        }
      }
      checkActive()
    })
  }

  // Reverse the timeline
  await tl.reverse()
}

// Run hide() after every route change
const router = useRouter()
router.afterEach(() => {
  hide()
})

onUnmounted(() => {
  tl?.kill() // Kill the timeline
})
</script>

<style scoped>
.br-router-link .router-link-active {
  text-decoration-line: underline;
  text-underline-offset: 0.625rem;
  text-decoration-thickness: 2px;
}

.br-router-link-exact .router-link-exact-active {
  text-decoration-line: underline;
  text-underline-offset: 0.625rem;
  text-decoration-thickness: 2px;
}
</style>

<style>
.navigation-br {
  .slide-up {
    @apply invisible;
    transform: translateY(2.75rem);
  }

  .burger-button {
    @apply visible;
  }

  .close-button {
    @apply invisible;
  }

  .slide-down {
    @apply invisible;
    transform-box: fill-box;
    transform: translateY(-2.75rem);
  }
}
</style>
